import type {
  Debt,
  DebtAllocation,
  IncomeSource,
  LedgerEntryWithTotals,
  LedgerStatus,
  MonthlyIncomeEntry,
  MonthlyLedgerEntry,
} from '@/types'
import { effectiveAPR } from './promoWatcher'
import { monthlyInterest } from './interestCalc'
import { SIMULATION_CLEARED_THRESHOLD } from './tolerances'
import { safeUUID } from '@/lib/uuid'
import { parseLocalMonth } from '@/lib/parseLocalDate'

// ─── Month string helpers ───────────────────────────────────────────

/** Convert a Date to "YYYY-MM" */
export function toMonthString(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

/** Get the current month as "YYYY-MM" */
export function currentMonth(): string {
  return toMonthString(new Date())
}

/** Add N months to a "YYYY-MM" string */
export function addMonths(month: string, n: number): string {
  const [yearStr = '0', monthStr = '0'] = month.split('-')
  const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, 1)
  date.setMonth(date.getMonth() + n)
  return toMonthString(date)
}

/** Format "YYYY-MM" as "April 2026" */
export function formatMonthLabel(month: string): string {
  const [yearStr = '0', monthStr = '0'] = month.split('-')
  const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

// ─── Ledger entry creation ──────────────────────────────────────────

/**
 * Build a fresh ledger entry for a given month from current debts and income
 * sources. Allocations default to each debt's minimum payment.
 *
 * Used for the very first ledger entry — when there's no prior month to copy from.
 */
export function buildBlankLedgerEntry(
  month: string,
  debts: Debt[],
  incomeSources: IncomeSource[]
): MonthlyLedgerEntry {
  // v0.8.44 — parseLocalMonth restores local-time anchoring; pre-fix
  // `new Date(\`${month}-01\`)` drifted to UTC-midnight which is the
  // prior day in US Pacific, breaking promo-expiry comparisons inside
  // effectiveAPR by ~5 hours at January boundaries.
  const startDate = parseLocalMonth(month)
  const allocations: DebtAllocation[] = debts
    .filter((d) => d.balance > 0)
    .map((d) => ({
      debtId: d.id,
      startingBalance: d.balance,
      effectiveAPR: effectiveAPR(d, 0, startDate),
      monthMinimumPayment: d.minimumPayment,
      plannedPayment: d.minimumPayment,
    }))

  const income: MonthlyIncomeEntry[] = incomeSources
    .filter((s) => s.isActive)
    .map((s) => ({
      sourceId: s.id,
      amount: s.defaultAmount ?? 0,
    }))

  return {
    id: safeUUID(),
    month,
    status: 'planning',
    income,
    allocations,
    createdAt: new Date().toISOString(),
  }
}

/**
 * Create the next month's ledger entry by copying from a previous one.
 *
 * - Allocations: copy plannedPayment from the previous month, but use the
 *   previous month's *ending balances* as the new starting balances. APRs are
 *   recalculated against the new month's start date so promo expirations
 *   advance correctly.
 * - Income: copy the previous month's amounts forward (Option B from the design).
 *
 * If the previous month is closed and has reconciled balances, those win.
 * If it's still planning (user is rolling forward early), use plannedPayment
 * to project what ending balances will likely be.
 */
export function buildNextMonthFromPrevious(
  previous: MonthlyLedgerEntry,
  debts: Debt[]
): MonthlyLedgerEntry {
  const nextMonth = addMonths(previous.month, 1)
  // v0.8.44 — parseLocalMonth restores local-time anchoring; see
  // parseLocalDate.ts for the full rationale. Pre-fix
  // `new Date(\`${nextMonth}-01\`)` drifted to UTC midnight.
  const nextStartDate = parseLocalMonth(nextMonth)

  const allocations: DebtAllocation[] = previous.allocations.map((prev) => {
    const debt = debts.find((d) => d.id === prev.debtId)
    if (!debt) {
      // Debt was deleted — keep allocation but mark balance as zero
      return {
        debtId: prev.debtId,
        startingBalance: 0,
        effectiveAPR: 0,
        monthMinimumPayment: 0,
        plannedPayment: 0,
      }
    }

    // Determine starting balance for the new month
    let startingBalance: number
    if (previous.status === 'closed') {
      // Closed month has authoritative ending balances
      startingBalance = prev.endingBalanceOverride ?? prev.endingBalanceCalculated ?? debt.balance
    } else {
      // Previous month still planning — project forward using plannedPayment
      const interest = monthlyInterest(prev.startingBalance, prev.effectiveAPR)
      startingBalance = Math.max(0, prev.startingBalance + interest - prev.plannedPayment)
    }

    return {
      debtId: prev.debtId,
      startingBalance,
      // v0.8.44 — pass simulationMonth=0 because nextStartDate is
      // already anchored at the right month. Pre-fix passed
      // `monthsBetween('2026-01', nextMonth)` AND nextStartDate; the
      // promoWatcher's `effectiveAPR` adds simulationMonth months to
      // startDate, so the result was double-advanced by the elapsed
      // months since 2026-01. A promo expiring Aug 2027, evaluated for
      // March 2027 planning, was treated as already expired (March
      // 2027 + 14 = May 2028 > Aug 2027) and projected with the
      // post-promo APR ~5 months early.
      effectiveAPR: effectiveAPR(debt, 0, nextStartDate),
      // Reset to registry minimum each month — user can override per month if statement differs
      monthMinimumPayment: debt.minimumPayment,
      plannedPayment: prev.plannedPayment, // copy planned forward
    }
  })

  return {
    id: safeUUID(),
    month: nextMonth,
    status: 'planning',
    income: previous.income.map((i) => ({ ...i })),
    // Note: essentialExpenses is intentionally NOT copied forward. Planning
    // entries derive essentials live from the recurring-expenses store; only
    // closed entries carry an essentialExpenses snapshot.
    allocations,
    createdAt: new Date().toISOString(),
  }
}

/** Number of months between two "YYYY-MM" strings (b - a) */
export function monthsBetween(a: string, b: string): number {
  const [ay = 0, am = 0] = a.split('-').map((s) => parseInt(s))
  const [by = 0, bm = 0] = b.split('-').map((s) => parseInt(s))
  return (by - ay) * 12 + (bm - am)
}

// ─── Closing a month ────────────────────────────────────────────────

/**
 * Reconcile and close a ledger entry for the month.
 *
 * For each allocation:
 *   - Resolve live registry values (startingBalance / effectiveAPR /
 *     monthMinimumPayment) and freeze them as snapshots — closed entries
 *     don't react to subsequent registry edits.
 *   - actualPayment defaults to plannedPayment if not set
 *   - interestAccrued = monthlyInterest(startingBalance, effectiveAPR)
 *   - endingBalanceCalculated = startingBalance + interest - actualPayment
 *   - endingBalanceOverride is preserved if user set it manually
 *   - monthMinimumPaymentOverride is cleared (already baked into the snapshot)
 *
 * Returns a new entry — does not mutate input.
 */
export function closeMonthEntry(
  entry: MonthlyLedgerEntry,
  debts: Debt[] = [],
  essentials?: number
): MonthlyLedgerEntry {
  const resolved = resolveEntryAllocations(entry, debts)
  const resolvedById = new Map(resolved.map((r) => [r.debtId, r]))
  const debtById = new Map(debts.map((d) => [d.id, d]))

  const allocations = entry.allocations.map((a) => {
    const r = resolvedById.get(a.debtId)
    const startingBalance = r?.startingBalance ?? a.startingBalance
    const effectiveAPR_ = r?.effectiveAPR ?? a.effectiveAPR
    const monthMinimumPayment = r?.monthMinimumPayment ?? a.monthMinimumPayment

    const actualPayment = a.actualPayment ?? a.plannedPayment
    const interestAccrued = monthlyInterest(startingBalance, effectiveAPR_)
    const endingBalanceCalculated = Math.max(0, startingBalance + interestAccrued - actualPayment)

    // v0.8.23: snapshot the registry's `debt.balance` JUST BEFORE this close
    // so the store's reopen action can restore it symmetrically. Use the
    // live registry value (not the allocation's startingBalance snapshot,
    // which is what we just computed for the closed entry); for PLANNING
    // entries those are typically the same number, but reading from `debts`
    // makes the intent ("save the registry's view of this debt") explicit.
    // If the debt was deleted between planning and close, leave undefined.
    const liveDebt = debtById.get(a.debtId)
    const balanceBeforeClose = liveDebt ? liveDebt.balance : undefined
    // v0.8.49: snapshot `paidOffAt` too. Close-month may auto-stamp the
    // field when the payment drives the balance to zero (v0.8.41); without
    // this snapshot, reopen restores the balance but leaves the stamp,
    // so the Registry shows a positive balance AND a "Paid Off" badge.
    // Tri-state: string (was stamped pre-close) / null (was unstamped
    // pre-close, reopen should clear auto-stamp) / undefined (debt gone
    // between planning and close).
    const paidOffAtBeforeClose: string | null | undefined = liveDebt
      ? (liveDebt.paidOffAt ?? null)
      : undefined

    return {
      ...a,
      startingBalance,
      effectiveAPR: effectiveAPR_,
      monthMinimumPayment,
      monthMinimumPaymentOverride: undefined,
      actualPayment,
      interestAccrued,
      endingBalanceCalculated,
      balanceBeforeClose,
      paidOffAtBeforeClose,
    }
  })

  // Snapshot the essentials value at close-time so the historical record stays
  // immutable and accurate to what was true that month — even if the
  // recurring-expenses registry changes later. If the caller didn't supply
  // a value, preserve whatever was already on the entry (legacy-compat path).
  const essentialExpenses = essentials !== undefined ? essentials : entry.essentialExpenses

  return {
    ...entry,
    status: 'closed',
    allocations,
    essentialExpenses,
    closedAt: new Date().toISOString(),
  }
}

// ─── Live-vs-frozen allocation resolution ───────────────────────────

/**
 * Effective values for a debt allocation, accounting for the "plan is live,
 * history is frozen" invariant.
 *
 * - PLANNING entries: derive `startingBalance`, `effectiveAPR`,
 *   `monthMinimumPayment` from the registry on every read. The
 *   `monthMinimumPaymentOverride` field on the allocation, when set, takes
 *   precedence over the registry's minimum (e.g. a statement minimum that
 *   differs from the registry default).
 * - CLOSED entries: use the snapshot values frozen at close-time.
 *
 * Always go through this resolver before using an allocation's values for
 * display, totals, or sorting.
 */
export interface ResolvedAllocation {
  debtId: string
  startingBalance: number
  effectiveAPR: number
  monthMinimumPayment: number
  /** True when planning + override is set; false when using registry default or closed snapshot */
  isMinimumOverridden: boolean
  /** Pointer back to the underlying allocation for plannedPayment/actualPayment/etc. */
  raw: DebtAllocation
}

export function resolveAllocation(
  allocation: DebtAllocation,
  debt: Debt | undefined,
  status: LedgerStatus,
  monthStartDate?: Date
): ResolvedAllocation {
  if (status === 'closed' || !debt) {
    return {
      debtId: allocation.debtId,
      startingBalance: allocation.startingBalance,
      effectiveAPR: allocation.effectiveAPR,
      monthMinimumPayment: allocation.monthMinimumPayment,
      isMinimumOverridden: false,
      raw: allocation,
    }
  }
  // v0.8.49 — planning entries now use `allocation.startingBalance` (the
  // snapshot at rollover / entry creation) rather than `debt.balance` (live).
  // Live registry sync via `setActualPayment` moves `debt.balance` mid-month
  // as the user logs actual payments; using it here would break close-month
  // interest math (interest is `f(start of month)`, not `f(current balance)`).
  // APR and minimum still come from the live registry — those don't drift
  // with logged payments, and users expect them to reflect current setup.
  const apr = effectiveAPR(debt, 0, monthStartDate ?? new Date())
  const overridden = allocation.monthMinimumPaymentOverride !== undefined
  return {
    debtId: allocation.debtId,
    startingBalance: allocation.startingBalance,
    effectiveAPR: apr,
    monthMinimumPayment: overridden ? allocation.monthMinimumPaymentOverride! : debt.minimumPayment,
    isMinimumOverridden: overridden,
    raw: allocation,
  }
}

export function resolveEntryAllocations(
  entry: MonthlyLedgerEntry,
  debts: Debt[]
): ResolvedAllocation[] {
  const debtMap = new Map(debts.map((d) => [d.id, d]))
  // v0.8.44 — local-time anchoring; see parseLocalDate.ts.
  const monthStartDate = parseLocalMonth(entry.month)
  return entry.allocations.map((a) =>
    resolveAllocation(a, debtMap.get(a.debtId), entry.status, monthStartDate)
  )
}

// ─── Totals & derived values ────────────────────────────────────────

/**
 * Compute all the rolled-up totals for a ledger entry.
 * Pure function — UI calls this once per entry per render via memoization.
 *
 * Uses `resolveAllocation` so PLANNING entries reflect live registry state and
 * CLOSED entries reflect their frozen snapshots.
 *
 * `essentials` is the resolved essential-expense burden for the entry's month.
 *   - For PLANNING entries: derive from the recurring-expenses store (this-month
 *     actual or steady-state average — the caller decides which).
 *   - For CLOSED entries: pass `entry.essentialExpenses ?? 0` (the snapshot
 *     frozen at close-time).
 *
 * The fallback to `entry.essentialExpenses ?? 0` if `essentials` is omitted is
 * a tests/legacy-callers convenience only — production hooks should always
 * compute and pass an explicit value.
 */
export function computeLedgerTotals(
  entry: MonthlyLedgerEntry,
  debts: Debt[],
  essentials?: number
): LedgerEntryWithTotals {
  const totalIncome = entry.income.reduce((s, i) => s + i.amount, 0)
  const resolved = resolveEntryAllocations(entry, debts)

  const totalPlanned = entry.allocations.reduce((s, a) => s + a.plannedPayment, 0)
  const totalActual = entry.allocations.reduce(
    (s, a) => s + (a.actualPayment ?? a.plannedPayment),
    0
  )
  const totalInterest = resolved.reduce(
    (s, r) => s + (r.raw.interestAccrued ?? monthlyInterest(r.startingBalance, r.effectiveAPR)),
    0
  )

  const totalPrincipalReduction = totalActual - totalInterest

  const minimums = resolved.reduce((s, r) => s + r.monthMinimumPayment, 0)

  const resolvedEssentials = essentials ?? entry.essentialExpenses ?? 0
  const survivalFloor = minimums + resolvedEssentials
  const surplus = totalIncome - resolvedEssentials - totalPlanned

  return {
    ...entry,
    totalIncome,
    totalPlanned,
    totalActual,
    totalInterest,
    totalPrincipalReduction,
    surplus,
    survivalFloor,
    resolvedEssentials,
  }
}

// ─── Quick-fill strategies ──────────────────────────────────────────

/**
 * Apply minimum payments to all allocations.
 *
 * Uses the resolved per-month minimum: an explicit override if set, else the
 * registry's current `debt.minimumPayment` for planning entries, else the
 * frozen snapshot for closed entries.
 */
export function applyMinimumsOnly(entry: MonthlyLedgerEntry, debts: Debt[]): MonthlyLedgerEntry {
  const resolved = resolveEntryAllocations(entry, debts)
  const minByDebt = new Map(resolved.map((r) => [r.debtId, r.monthMinimumPayment]))
  const allocations = entry.allocations.map((a) => ({
    ...a,
    plannedPayment: minByDebt.get(a.debtId) ?? 0,
  }))
  return { ...entry, allocations }
}

/**
 * Distribute a surplus on top of minimums using one of the engine strategies.
 *
 * For Phase 1 we implement avalanche (highest APR first) directly here. The
 * full strategy engine is for *projections* — for a single month's allocation
 * we just need to know "where does the extra dollar go this month?"
 *
 * @param strategy - 'avalanche' (highest APR) or 'snowball' (lowest balance)
 */
export function applyStrategySurplus(
  entry: MonthlyLedgerEntry,
  debts: Debt[],
  surplus: number,
  strategy: 'avalanche' | 'snowball'
): MonthlyLedgerEntry {
  const withMinimums = applyMinimumsOnly(entry, debts)
  const resolved = resolveEntryAllocations(withMinimums, debts)
  const resolvedById = new Map(resolved.map((r) => [r.debtId, r]))

  const sortable = resolved.filter((r) => r.startingBalance > 0)
  if (strategy === 'avalanche') {
    sortable.sort((a, b) => b.effectiveAPR - a.effectiveAPR)
  } else {
    sortable.sort((a, b) => a.startingBalance - b.startingBalance)
  }

  let remaining = surplus
  const allocationsById = new Map(withMinimums.allocations.map((a) => [a.debtId, { ...a }]))

  for (const r of sortable) {
    if (remaining <= SIMULATION_CLEARED_THRESHOLD) break
    const target = allocationsById.get(r.debtId)
    const resolvedTarget = resolvedById.get(r.debtId)
    if (!target || !resolvedTarget) continue
    // Cap payment at balance + interest (no overpayment in a single month)
    const interest = monthlyInterest(resolvedTarget.startingBalance, resolvedTarget.effectiveAPR)
    const maxPayment = resolvedTarget.startingBalance + interest
    const room = maxPayment - target.plannedPayment
    const extra = Math.max(0, Math.min(remaining, room))
    target.plannedPayment += extra
    remaining -= extra
  }

  return {
    ...withMinimums,
    allocations: Array.from(allocationsById.values()),
  }
}

// ─── History & trajectory helpers ───────────────────────────────────

/**
 * A single point on the actual trajectory line.
 *
 * `totalEndingBalance` is the sum of effective ending balances across all
 * allocations in a closed entry — this is what the user actually owes at the
 * end of that month, reflecting reconciled values from bank statements when
 * available.
 *
 * `isInProgress` (v0.8.49) marks the last data point when it came from the
 * currently-active planning month (as opposed to a closed month). The UI
 * renders it as a semi-transparent / dashed indicator so the user can see
 * mid-month progress without conflating it with reconciled history.
 */
export interface ActualTrajectoryPoint {
  month: string
  totalEndingBalance: number
  totalIncome: number
  totalActual: number
  totalInterest: number
  isInProgress?: boolean
}

/**
 * Build the actual trajectory from monthly ledger entries.
 *
 * Includes:
 *   - Every CLOSED entry (authoritative history)
 *   - The active PLANNING entry IF at least one allocation has an
 *     `actualPayment` logged. This surfaces mid-month progress in the
 *     Projections chart / History tab so the user gets immediate reward
 *     for logging payments — no need to wait for close-month.
 *
 * Sorted by month ascending. Returns an empty array if there's no useful data.
 *
 * When `activeMonth` and `debts` are omitted, the function reduces to the
 * pre-v0.8.49 closed-only behavior for backward compatibility.
 */
export function computeActualTrajectory(
  entries: MonthlyLedgerEntry[],
  activeMonth?: string,
  debts?: Debt[]
): ActualTrajectoryPoint[] {
  const closed = entries
    .filter((e) => e.status === 'closed')
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((e): ActualTrajectoryPoint => {
      const totalEndingBalance = e.allocations.reduce((s, a) => {
        // Override wins; otherwise calculated; otherwise we don't know
        const ending = a.endingBalanceOverride ?? a.endingBalanceCalculated ?? a.startingBalance
        return s + ending
      }, 0)
      const totalIncome = e.income.reduce((s, i) => s + i.amount, 0)
      const totalActual = e.allocations.reduce(
        (s, a) => s + (a.actualPayment ?? a.plannedPayment),
        0
      )
      const totalInterest = e.allocations.reduce((s, a) => s + (a.interestAccrued ?? 0), 0)
      return {
        month: e.month,
        totalEndingBalance,
        totalIncome,
        totalActual,
        totalInterest,
      }
    })

  if (!activeMonth || !debts) return closed

  const activePlanning = entries.find((e) => e.month === activeMonth && e.status === 'planning')
  if (!activePlanning) return closed
  const hasLoggedPayment = activePlanning.allocations.some(
    (a) => a.actualPayment !== undefined && a.actualPayment !== null
  )
  if (!hasLoggedPayment) return closed
  // Avoid duplicate points if a closed entry somehow shares the active month
  if (closed.some((p) => p.month === activePlanning.month)) return closed

  const resolved = resolveEntryAllocations(activePlanning, debts)
  const inProgress: ActualTrajectoryPoint = {
    month: activePlanning.month,
    // Sum of (start-of-month snapshot) + (accrued interest so far) - (payments logged).
    // Unpaid allocations contribute their full snapshot + interest so the
    // total honestly reflects "what you'd owe end-of-month if you paid
    // nothing else from here."
    totalEndingBalance: resolved.reduce((s, r) => {
      const alloc = r.raw
      const interest = alloc.interestAccrued ?? monthlyInterest(r.startingBalance, r.effectiveAPR)
      const paid = alloc.actualPayment ?? 0
      return s + Math.max(0, r.startingBalance + interest - paid)
    }, 0),
    totalIncome: activePlanning.income.reduce((s, i) => s + i.amount, 0),
    totalActual: activePlanning.allocations.reduce((s, a) => s + (a.actualPayment ?? 0), 0),
    totalInterest: resolved.reduce(
      (s, r) => s + (r.raw.interestAccrued ?? monthlyInterest(r.startingBalance, r.effectiveAPR)),
      0
    ),
    isInProgress: true,
  }

  return [...closed, inProgress]
}

/**
 * Year-to-date totals across closed monthly entries.
 *
 * "Year" is identified by the first 4 chars of the month string. Pass the
 * current year (e.g. '2026') to get YTD; pass any historical year for that
 * year's totals.
 */
export interface YTDTotals {
  year: string
  monthsClosed: number
  totalPaid: number
  totalInterest: number
  totalPrincipalReduction: number
  totalIncome: number
}

export function computeYTDTotals(entries: MonthlyLedgerEntry[], year: string): YTDTotals {
  const closedInYear = entries.filter((e) => e.status === 'closed' && e.month.startsWith(year))

  const totals: YTDTotals = {
    year,
    monthsClosed: closedInYear.length,
    totalPaid: 0,
    totalInterest: 0,
    totalPrincipalReduction: 0,
    totalIncome: 0,
  }

  for (const e of closedInYear) {
    const paid = e.allocations.reduce((s, a) => s + (a.actualPayment ?? a.plannedPayment), 0)
    const interest = e.allocations.reduce((s, a) => s + (a.interestAccrued ?? 0), 0)
    const income = e.income.reduce((s, i) => s + i.amount, 0)
    totals.totalPaid += paid
    totals.totalInterest += interest
    totals.totalPrincipalReduction += paid - interest
    totals.totalIncome += income
  }

  return totals
}

/**
 * Streak of consecutive closed months where the user was at or above their
 * survival floor (i.e. income covered minimums + essentials).
 *
 * Returns both the current active streak and the longest historical streak.
 * Closed months are evaluated in chronological order. A month BELOW the floor
 * resets the current streak to zero. The longest streak persists.
 */
export interface StreakInfo {
  current: number
  longest: number
  floorMonths: number // total months above floor
  belowFloorMonths: number // total months below floor
}

export function computeSurvivalFloorStreak(
  entries: MonthlyLedgerEntry[],
  debts: Debt[]
): StreakInfo {
  const closedSorted = entries
    .filter((e) => e.status === 'closed')
    .sort((a, b) => a.month.localeCompare(b.month))

  let current = 0
  let longest = 0
  let floorMonths = 0
  let belowFloorMonths = 0

  for (const entry of closedSorted) {
    const totals = computeLedgerTotals(entry, debts)
    const aboveFloor = totals.totalIncome >= totals.survivalFloor
    if (aboveFloor) {
      current += 1
      floorMonths += 1
      if (current > longest) longest = current
    } else {
      belowFloorMonths += 1
      current = 0
    }
  }

  return { current, longest, floorMonths, belowFloorMonths }
}

/**
 * Build a unified chart-ready dataset combining the actual trajectory (from
 * closed ledger entries) with a projected trajectory (from the engine
 * simulation snapshots).
 *
 * The two lines share the same x-axis (months in YYYY-MM format). Each point
 * has up to two values: `actual` (if a closed entry exists for that month)
 * and `projected` (if the simulation runs through that month).
 *
 * The chart starts at the earliest closed month if any exist, otherwise at
 * the current simulation start. It ends at whichever is later: the last
 * projected month or the last actual month.
 */
export interface TrajectoryChartPoint {
  month: string
  monthLabel: string // human-readable, e.g. "Apr 2026"
  /** Sum of ending balances for a closed month */
  actual?: number
  /**
   * Sum of "would-owe-end-of-month" for the active planning month with at
   * least one logged payment. Split from `actual` so the chart can render
   * the in-progress point with a different dot / stroke without losing the
   * clean history line.
   */
  actualInProgress?: number
  projected?: number
}

export function buildTrajectoryDataset(
  actualTrajectory: ActualTrajectoryPoint[],
  projectedSnapshots: { date: string; totalBalance: number }[]
): TrajectoryChartPoint[] {
  // Build a map keyed by YYYY-MM
  const byMonth = new Map<string, TrajectoryChartPoint>()

  // Seed with all actual months. In-progress point goes on `actualInProgress`
  // so the closed-history line stays a clean solid stroke and the in-progress
  // dot can be rendered separately by the chart.
  for (const a of actualTrajectory) {
    if (a.isInProgress) {
      byMonth.set(a.month, {
        month: a.month,
        monthLabel: formatMonthLabel(a.month),
        actualInProgress: a.totalEndingBalance,
      })
    } else {
      byMonth.set(a.month, {
        month: a.month,
        monthLabel: formatMonthLabel(a.month),
        actual: a.totalEndingBalance,
      })
    }
  }

  // Add projected — note: projection snapshots use the YYYY-MM format already
  for (const p of projectedSnapshots) {
    const existing = byMonth.get(p.date)
    if (existing) {
      existing.projected = p.totalBalance
    } else {
      byMonth.set(p.date, {
        month: p.date,
        monthLabel: formatMonthLabel(p.date),
        projected: p.totalBalance,
      })
    }
  }

  // Sort by month and return
  return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month))
}
